import type {
  ShiftTime,
  TimeOfDayOccupancyRating,
} from 'restaurantAdmin/operations/pricing/types';
import type { Shift } from '@shared/types/shifts';
import { max, min } from 'date-fns';
import { createReferenceDate } from './createReferenceDate';

/**
 * Splits shifts that start and end on different days at midnight, returning new
 * shifts that fall within a single day (with an adjusted repeat value).
 *
 * Example:
 * - A shift that starts at 22:00, ends at 02:00, and has a repeat value of
 *   ['0'] (Sunday) would be split into:
 *   two shifts:
 *   - 22:00-23:59 on Sunday (repeat includes '0' for Sunday)
 *   - 00:00-02:00 on Monday (repeat includes '1' for Monday)
 *
 * @param shifts - The list of shifts.
 * @returns A virtual list of shifts split at midnight.
 */

export const splitShiftsAtMidnight = (shifts: Shift[]): Shift[] => {
  const BEGINNING_OF_DAY = '00:00:00';
  const END_OF_DAY = '23:59:59';

  return shifts.flatMap((shift) => {
    if (shift.endTime < shift.startTime) {
      return [
        {
          ...shift,
          endTime: END_OF_DAY,
        },
        {
          ...shift,
          startTime: BEGINNING_OF_DAY,
          repeat: shift.repeat.map(getNextDayOfWeek),
        },
      ];
    }

    return [shift];
  });
};

export const shiftRepeatsOnDate = (
  shift: Pick<Shift, 'repeat'>,
  date: Date,
) => {
  const dayOfWeek = date.getDay().toString();
  return shift.repeat.includes(dayOfWeek);
};

export const getNextDayOfWeek = (repeatDay: string) =>
  repeatDay === '6' ? '0' : (parseInt(repeatDay, 10) + 1).toString();

export const getEarliestStartTime = (
  shifts: ShiftTime[],
  date: Date = new Date(),
) => {
  const allStartTimes = shifts.map((shift) =>
    createReferenceDate(shift.startTime, date),
  );

  if (allStartTimes.length === 0) return date;

  return min(allStartTimes);
};

export const getLatestEndTime = (
  shifts: ShiftTime[],
  date: Date = new Date(),
): Date => {
  const allEndTimes = shifts.map((shift) =>
    createReferenceDate(shift.endTime, date),
  );

  if (allEndTimes.length === 0) return date;

  return max(allEndTimes);
};

/**
 * Checks if a given ISO time string falls within any of the provided shift
 * start and end times.
 *
 * Assumes shifts do not span across midnight and have already been filtered for
 * the relevant day.
 */
export const isWithinShiftTimeRange = (
  time: string,
  shifts: ShiftTime[] = [],
) =>
  shifts.some(({ startTime, endTime }) => time >= startTime && time <= endTime);

/**
 * Checks if a given time and day of week falls within any of the provided shift
 * start and end times as well as repeat values.
 */
export const isWithinShiftTimeRangeAndRepeat = (
  { dayOfWeek, time }: Pick<TimeOfDayOccupancyRating, 'dayOfWeek' | 'time'>,
  shifts: Pick<Shift, 'repeat' | 'startTime' | 'endTime'>[],
) => {
  const shiftsForRatingDayOfWeek = shifts.filter((s) =>
    s.repeat.includes(dayOfWeek.toString()),
  );

  return shiftsForRatingDayOfWeek.some(
    ({ startTime, endTime }) => time >= startTime && time <= endTime,
  );
};
